// SHEventTickets.js
import React, { useContext, useEffect, useState } from 'react';
import SHTicketCard from '../../../components/SHTicketCard/SHTicketCard';
import { TbMapPin, TbWorld } from 'react-icons/tb';
import { useLanguage } from '../../../../../LanguageProvider';
import AddTicketModal from '../../../components/AddTicketModal/AddTicketModal';
import Services from '../../../../../services/Services';
import { useToaster } from '../../../../../ToasterProvider';
import { GeneralContext } from '../../../../../GeneralProvider';
import AlertBox from '../../../../../components/AlertBox/AlertBox';

export default function SHEventTickets({ event, ticketTypes, ticketInfosMap, handleTicketChange, isAdmin, getEventDetails, getEvents, handleReserveSpot }) {
  const token = localStorage.getItem("token");
  const { toastError, toastSuccess } = useToaster();
  const { getTranslation } = useLanguage();
  const [openModal, setOpenModal] = useState(false);
  const { setEventIDUri } = useContext(GeneralContext);

  const [alertOpen, setAlertOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);

  // Validate uniqueness of email and phone across all ticket entries
  const validateUnique = (ticketID, newInfos) => {
    const combined = { ...ticketInfosMap, [ticketID]: newInfos };
    const seenEmails = new Set();
    const seenPhones = new Set();

    for (const infos of Object.values(combined)) {
      for (const { email, phone } of infos) {
        if (email) {
          if (seenEmails.has(email)) {
            toastError(getTranslation("duplicateEmailError") || "Her e-posta yalnızca bir kez kullanılabilir");
            return false;
          }
          seenEmails.add(email);
        }
        if (phone) {
          if (seenPhones.has(phone)) {
            toastError(getTranslation("duplicatePhoneError") || "Her telefon numarası yalnızca bir kez kullanılabilir");
            return false;
          }
          seenPhones.add(phone);
        }
      }
    }
    return true;
  };

  useEffect(() => {
    setEventIDUri("")
    handleReserveSpot()
  }, []);

  // Wrapped change handler
  const onInfosChange = (ticketID, infos) => {
    if (!validateUnique(ticketID, infos)) return;
    handleTicketChange(ticketID, infos);
  };

  const removeTicket = (eventID, ticketID) => {
    Services.SocialHiveRemoveTicketFromEvent(token, eventID, ticketID)
      .then((res) => {
        if (res.data.status) {
          toastSuccess(getTranslation("successMessage"));
          getEventDetails();
        } else {
          toastError(getTranslation("somethingWentWrong"));
        }
      })
      .catch((err) => {
        console.error(err);
        toastError(getTranslation("somethingWentWrong"));
      });
  };

  const confirmDelete = () => {
    if (ticketToDelete) {
      removeTicket(event.eventID, ticketToDelete);
      setTicketToDelete(null);
    }
    setAlertOpen(false);
  };

  const isSalesEnded = (salesEnd) => {
    if (!salesEnd) return false;

    // 1) Eğer number ise: ms mi, saniye mi ayırt et
    if (typeof salesEnd === "number") {
      const ms = salesEnd < 1e12 ? salesEnd * 1000 : salesEnd; // 10 haneli -> saniye, 13 haneli -> ms
      return Date.now() > ms;
    }

    // 2) Eğer string ise: "1700000000" gibi numeric string olabilir
    if (typeof salesEnd === "string" && /^\d+$/.test(salesEnd.trim())) {
      const n = Number(salesEnd.trim());
      const ms = n < 1e12 ? n * 1000 : n;
      return Date.now() > ms;
    }

    // 3) ISO / Date parse edilebilir string ise
    const t = new Date(salesEnd).getTime();
    if (Number.isNaN(t)) return false;

    return Date.now() > t;
  };

  return (
    <div className="sh-info-main" style={{ height: 'fit-content' }}>
      <h1 className="sh-event-title">{event?.title}</h1>

      {isAdmin === "1" &&
        <button
          onClick={() => setOpenModal(true)}
          className="sh-ticket-card-btn"
          style={{ marginLeft: 'auto', width: 'auto', fontSize: '12px', padding: '5px' }}
        >
          {getTranslation("addTicketType")}
        </button>
      }

      {ticketTypes
        ?.slice() // original state'i mutate etmemek için yüzeysel kopyalama
        .sort((a, b) => a.price - b.price) // fiyata göre artacak şekilde düzenle
        .map(ticket => (
          <SHTicketCard
            key={ticket.id}
            title={ticket.title}
            description={ticket.description}
            price={ticket.price}
            salesEnd={event.eventEndDateTime}
            isSalesEnded={isSalesEnded(event.eventEndDateTime)}
            ticketInfos={ticketInfosMap[ticket.ticketID] || []}
            onTicketChange={infos => onInfosChange(ticket.ticketID, infos)}
            currency={ticket.currency}
            removeTicket={() => {
              setTicketToDelete(ticket.ticketID);
              setAlertOpen(true);
            }}
            isAdmin={isAdmin}
            type={ticket.type}
            ticket={ticket}
            ticketInfosMap={ticketInfosMap}
          />
        ))
      }

      <AddTicketModal
        channelID={event?.channelID}
        eventID={event?.eventID}
        getEventDetails={getEventDetails}
        isOpen={openModal}
        tickets={ticketTypes}
        onClose={() => setOpenModal(false)}
        getEvents={getEvents}
      />

      <AlertBox
        isOpen={alertOpen}
        title={getTranslation("deleteTicketTitle")}
        message={getTranslation("deleteTicketConfirm")}
        confirmText={getTranslation("yes")}
        cancelText={getTranslation("no")}
        type="warning"
        onConfirm={confirmDelete}
        onCancel={() => setAlertOpen(false)}
      />
    </div>
  );
}
